import { useState, useEffect } from "react";
import { Download, Share2, Loader2 } from "lucide-react";
import { usePwaInstallPrompt } from "@/hooks/use-pwa-install";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@/components/ui/alert";
import { toast } from "sonner";

export const PwaInstallButton = () => {
  const { canInstall, isInstalled, isIOS, promptInstall } = usePwaInstallPrompt();
  const [showIOSInstructions, setShowIOSInstructions] = useState(false);
  const [isInstalling, setIsInstalling] = useState(false);

  // Listen for successful installation
  useEffect(() => {
    const handlePwaInstalled = () => {
      toast.success("App Installed Successfully! 🎉", {
        description: "You can now open the app from your home screen. It works offline too!",
        duration: 6000,
      });
    };

    window.addEventListener('pwa-installed', handlePwaInstalled);
    return () => {
      window.removeEventListener('pwa-installed', handlePwaInstalled);
    };
  }, []);

  if (isInstalled || !canInstall) {
    return null;
  }

  const handleInstallClick = async () => {
    if (isIOS) {
      setShowIOSInstructions(true);
    } else {
      // Android/Chrome - trigger immediate install prompt
      // The native browser prompt will appear immediately
      setIsInstalling(true);
      try {
        const accepted = await promptInstall();
        if (accepted) {
          // Success feedback - the appinstalled event will also trigger a toast
          toast.success("Installation in Progress! 🚀", {
            description: "The app icon is being added to your home screen. You can open it like any other app!",
            duration: 5000,
          });
        } else {
          // User cancelled - no need to show error, just reset state
          setIsInstalling(false);
        }
      } catch (error) {
        console.error("Install error:", error);
        toast.error("Installation failed", {
          description: "Please try again or use your browser's menu to install the app.",
          duration: 4000,
        });
        setIsInstalling(false);
      }
    }
  };

  return (
    <>
      <Button
        onClick={handleInstallClick}
        variant={isIOS ? "outline" : "default"}
        size="sm"
        className={`gap-2 ${!isIOS ? "bg-primary text-primary-foreground hover:bg-primary/90 shadow-md" : ""}`}
        disabled={isInstalling}
      >
        {isInstalling ? (
          <>
            <Loader2 className="h-4 w-4 animate-spin" />
            Installing...
          </>
        ) : (
          <>
            <Download className="h-4 w-4" />
            {isIOS ? "Install App" : "Install App"}
          </>
        )}
      </Button>

      <Dialog open={showIOSInstructions} onOpenChange={setShowIOSInstructions}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Install Alraheem App</DialogTitle>
            <DialogDescription>
              Follow these steps to install the app on your iPhone or iPad
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <Alert>
              <Share2 className="h-4 w-4" />
              <AlertTitle>Step 1: Tap the Share Button</AlertTitle>
              <AlertDescription className="mt-2">
                Look for the share button <Share2 className="h-4 w-4 inline" /> at the bottom of your Safari browser
              </AlertDescription>
            </Alert>

            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary font-semibold">
                2
              </div>
              <div>
                <p className="font-medium">Step 2: Select "Add to Home Screen"</p>
                <p className="text-sm text-muted-foreground mt-1">
                  Scroll down in the share menu and tap "Add to Home Screen"
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary font-semibold">
                3
              </div>
              <div>
                <p className="font-medium">Step 3: Confirm Installation</p>
                <p className="text-sm text-muted-foreground mt-1">
                  Tap "Add" in the top right corner to complete the installation
                </p>
              </div>
            </div>

            <div className="pt-2 border-t">
              <p className="text-xs text-muted-foreground">
                Once installed, you can open the app from your home screen like any other app!
              </p>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
